import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { extractApiErrorMessage } from "@/lib/api/errors";
import {
  existingSkillFromConflict,
  skillsAPI,
  type SkillSuggestion,
  type SkillSuggestionOutcome,
} from "@/lib/api/skills";
import type {
  SkillNoMatchAction,
  SkillSuggestOutcome,
} from "@/components/skills/SkillsAutocomplete";

/** Maps the backend's suggestion outcome onto what the picker shows. */
export const toPickerOutcome = (
  text: string,
  outcome: SkillSuggestionOutcome
): SkillSuggestOutcome => {
  if (outcome.outcome === "existing_skill" && outcome.skill) {
    return { status: "existing", skill: { id: outcome.skill.id, name: outcome.skill.name } };
  }
  return {
    status: "pending",
    text: outcome.suggestion?.text ?? text,
    alreadySuggested: outcome.outcome === "already_suggested",
  };
};

/**
 * The admin "Create skill" action: checks for similar skills first, and turns
 * a 409 duplicate into "use the existing skill" (backend#992).
 */
export const useAdminCreateSkillAction = (): SkillNoMatchAction =>
  useMemo(
    () => ({
      kind: "create",
      findSimilar: skillsAPI.findSimilar,
      create: async (name: string) => {
        try {
          const created = await skillsAPI.create(name);
          toast.success(`Skill "${created.name}" created`);
          return created;
        } catch (err) {
          const clash = existingSkillFromConflict(err);
          if (clash) {
            toast.info(`"${name}" already exists as ${clash.skill.name}. Added that instead.`);
            return clash.skill;
          }
          toast.error(extractApiErrorMessage(err, "Failed to create skill"));
          throw err;
        }
      },
    }),
    []
  );

interface SuggestActionResult {
  action: SkillNoMatchAction;
  /** Texts of suggestions still awaiting review, for the pending chips. */
  pending: string[];
}

/**
 * The consultant "Suggest it" action plus their suggestions awaiting review
 * (backend#992). `submit` and `loadPending` let the signup flow reuse it with
 * its public, session-scoped endpoints.
 */
export const useSuggestSkillAction = (
  submit: (text: string) => Promise<SkillSuggestionOutcome> = skillsAPI.suggest,
  loadPending: (() => Promise<SkillSuggestion[]>) | null = skillsAPI.myPendingSuggestions
): SuggestActionResult => {
  const [pending, setPending] = useState<string[]>([]);

  useEffect(() => {
    if (!loadPending) {
      return;
    }
    let cancelled = false;
    // Deferred so a synchronous throw lands in the catch below too.
    Promise.resolve()
      .then(loadPending)
      .then(items => {
        if (!cancelled) {
          setPending(items.filter(s => s.status === "pending").map(s => s.text));
        }
      })
      .catch(err => console.warn("Failed to load pending skill suggestions:", err));
    return () => {
      cancelled = true;
    };
  }, [loadPending]);

  const suggest = useCallback(
    async (text: string) => {
      const outcome = toPickerOutcome(text, await submit(text));
      if (outcome.status === "pending") {
        setPending(prev => (prev.includes(outcome.text) ? prev : [...prev, outcome.text]));
      }
      return outcome;
    },
    [submit]
  );

  const action = useMemo<SkillNoMatchAction>(() => ({ kind: "suggest", suggest }), [suggest]);
  return { action, pending };
};
